"use client";

import { useState } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend, Cell,
} from "recharts";
import {
  ModelInfo, LANGUAGES, MODEL_REGISTRY, MODEL_COLORS,
  OUR_MODELS, BASELINE_MODELS,
  MetricTier, METRIC_TIERS, METRIC_LABELS,
  PRIMARY_METRIC_TIERS,
} from "@/lib/types";

type CompareData = Record<string, Record<string, Record<string, Record<string, number>>>>;

interface BaselinesViewProps {
  models: ModelInfo[];
  compareData: CompareData;
  selectedMetric: MetricTier;
}

interface ModelCheckpoint {
  modelId: string;
  checkpoint: string;
  label: string;
}

export default function BaselinesView({ models, compareData, selectedMetric: initialMetric }: BaselinesViewProps) {
  const [metric, setMetric] = useState<MetricTier>(initialMetric);

  // Build list of all available model+checkpoint combos
  const allOptions: ModelCheckpoint[] = [];
  for (const m of models) {
    if (m.checkpoints.length === 0) continue;
    for (const ckpt of m.checkpoints) {
      if (!compareData[m.id]?.[ckpt]) continue;
      allOptions.push({
        modelId: m.id,
        checkpoint: ckpt,
        label: `${m.displayName}${m.checkpoints.length > 1 ? ` (${ckpt})` : ""}`,
      });
    }
  }

  // Enabled state — all baselines on by default, our latest checkpoints on
  const [enabled, setEnabled] = useState<Record<string, boolean>>(() => {
    const state: Record<string, boolean> = {};
    for (const opt of allOptions) {
      const isBaseline = (BASELINE_MODELS as readonly string[]).includes(opt.modelId);
      const isOurs = (OUR_MODELS as readonly string[]).includes(opt.modelId);
      const isLatest = opt.checkpoint === models.find((m) => m.id === opt.modelId)?.checkpoints.slice(-1)[0];
      state[`${opt.modelId}/${opt.checkpoint}`] = isBaseline || (isOurs && isLatest);
    }
    return state;
  });

  const toggle = (key: string) => setEnabled((prev) => ({ ...prev, [key]: !prev[key] }));

  // Active entries
  const active = allOptions.filter((o) => enabled[`${o.modelId}/${o.checkpoint}`]);

  // Build overall comparison data
  const overallData = active.map((o) => {
    const tierData = compareData[o.modelId]?.[o.checkpoint]?.["__overall__"];
    return {
      label: o.label,
      modelId: o.modelId,
      value: tierData?.[metric] ?? 0,
    };
  }).sort((a, b) => a.value - b.value);

  // Build per-language comparison
  const langData = [...LANGUAGES].map((lang) => {
    const row: Record<string, string | number> = { language: lang };
    for (const o of active) {
      const key = `${o.modelId}/${o.checkpoint}`;
      const tierData = compareData[o.modelId]?.[o.checkpoint]?.[lang];
      row[key] = tierData?.[metric] ?? 0;
    }
    return row;
  });

  return (
    <div className="space-y-6 max-w-[1400px]">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-lg font-bold text-[var(--color-foreground)]">
            vs API Baselines
          </h2>
          <p className="text-xs text-[var(--color-muted)] mt-1">
            Compare our models against industry API baselines — same dataset, same normalization
          </p>
        </div>
        {/* Metric selector */}
        <div className="flex items-center gap-1 bg-[var(--color-card)] border border-[var(--color-border)] rounded-lg p-0.5">
          {METRIC_TIERS.map((tier) => (
            <button
              key={tier}
              onClick={() => setMetric(tier)}
              className={`px-3 py-1.5 rounded text-[10px] transition-colors ${
                metric === tier
                  ? "bg-[var(--color-card-hover)] text-[var(--color-foreground)]"
                  : "text-[var(--color-muted)] hover:text-[var(--color-foreground)]"
              }`}
            >
              {METRIC_LABELS[tier]}
            </button>
          ))}
        </div>
      </div>

      {/* Toggle chips */}
      <div className="flex flex-wrap gap-2">
        {allOptions.map((o) => {
          const key = `${o.modelId}/${o.checkpoint}`;
          const isOn = enabled[key];
          const isOurs = (OUR_MODELS as readonly string[]).includes(o.modelId);
          return (
            <button
              key={key}
              onClick={() => toggle(key)}
              className={`px-3 py-1.5 rounded-full text-[10px] border transition-colors flex items-center gap-1.5 ${
                isOn
                  ? "border-[var(--color-border-light)] text-[var(--color-foreground)] bg-[var(--color-card)]"
                  : "border-[var(--color-border)] text-[var(--color-muted)] bg-transparent opacity-50"
              }`}
            >
              <span
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: MODEL_COLORS[o.modelId] || "#6366f1" }}
              />
              {o.label}
              {isOurs && <span className="text-[8px] px-1 py-0 rounded bg-[var(--color-card-hover)]">ours</span>}
            </button>
          );
        })}
      </div>

      {/* Overall ranking bar chart */}
      {overallData.length > 0 && (
        <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-lg p-5">
          <h3 className="text-xs font-medium text-[var(--color-foreground)] tracking-wide mb-4">
            Overall {METRIC_LABELS[metric]} — Ranked
          </h3>
          <ResponsiveContainer width="100%" height={Math.max(200, overallData.length * 40)}>
            <BarChart data={overallData} layout="vertical" margin={{ top: 5, right: 30, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={(v: number) => `${v}%`} axisLine={false} tickLine={false} />
              <YAxis
                type="category"
                dataKey="label"
                tick={{ fontSize: 10 }}
                width={180}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                formatter={(value: unknown) => [`${Number(value).toFixed(2)}%`, METRIC_LABELS[metric]]}
                contentStyle={{
                  background: "#18181b",
                  border: "1px solid #3f3f46",
                  borderRadius: "8px",
                  fontSize: "11px",
                }}
              />
              <Bar dataKey="value" radius={[0, 4, 4, 0]} maxBarSize={24}>
                {overallData.map((entry, i) => {
                  const isOurs = (OUR_MODELS as readonly string[]).includes(entry.modelId);
                  return (
                    <Cell
                      key={i}
                      fill={MODEL_COLORS[entry.modelId] || "#6366f1"}
                      stroke={isOurs ? "#fff" : "none"}
                      strokeWidth={isOurs ? 1.5 : 0}
                    />
                  );
                })}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Per-language grouped bar chart */}
      {active.length > 0 && (
        <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-lg p-5">
          <h3 className="text-xs font-medium text-[var(--color-foreground)] tracking-wide mb-4">
            {METRIC_LABELS[metric]} by Language — All Selected Models
          </h3>
          <ResponsiveContainer width="100%" height={420}>
            <BarChart data={langData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="language"
                tick={{ fontSize: 9 }}
                tickFormatter={(v: string) => v.slice(0, 3).toUpperCase()}
                axisLine={false}
                tickLine={false}
              />
              <YAxis tick={{ fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={(v: number) => `${v}%`} />
              <Tooltip
                formatter={(value: unknown, name: unknown) => {
                  const key = String(name);
                  const opt = active.find((o) => `${o.modelId}/${o.checkpoint}` === key);
                  return [`${Number(value).toFixed(2)}%`, opt?.label || key];
                }}
                contentStyle={{
                  background: "#18181b",
                  border: "1px solid #3f3f46",
                  borderRadius: "8px",
                  fontSize: "10px",
                }}
              />
              <Legend
                wrapperStyle={{ fontSize: "9px", paddingTop: "8px" }}
                formatter={(value: string) => {
                  const opt = active.find((o) => `${o.modelId}/${o.checkpoint}` === value);
                  return opt?.label || value;
                }}
              />
              {active.map((o) => (
                <Bar
                  key={`${o.modelId}/${o.checkpoint}`}
                  dataKey={`${o.modelId}/${o.checkpoint}`}
                  fill={MODEL_COLORS[o.modelId] || "#6366f1"}
                  radius={[2, 2, 0, 0]}
                  maxBarSize={16}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Comparison table */}
      {active.length > 0 && (
        <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-lg p-5 overflow-x-auto">
          <h3 className="text-xs font-medium text-[var(--color-foreground)] tracking-wide mb-4">
            {METRIC_LABELS[metric]} — Full Comparison Table
          </h3>
          <table className="w-full text-[11px]">
            <thead>
              <tr className="border-b border-[var(--color-border)]">
                <th className="text-left font-normal text-[var(--color-muted)] pb-2 pr-4 sticky left-0 bg-[var(--color-card)]">Language</th>
                {active.map((o) => {
                  const isOurs = (OUR_MODELS as readonly string[]).includes(o.modelId);
                  return (
                    <th key={`${o.modelId}/${o.checkpoint}`} className="text-right font-normal pb-2 px-2 whitespace-nowrap" style={{ color: MODEL_COLORS[o.modelId] }}>
                      {MODEL_REGISTRY[o.modelId] || o.modelId}{isOurs ? "*" : ""}
                      {o.checkpoint !== "baseline" ? ` (${o.checkpoint})` : ""}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {[...LANGUAGES, "__overall__" as const].map((lang) => {
                const isOverall = lang === "__overall__";
                const values = active.map((o) => {
                  const tierData = compareData[o.modelId]?.[o.checkpoint]?.[lang];
                  return tierData?.[metric] ?? 0;
                });
                const minVal = Math.min(...values.filter((v) => v > 0));

                return (
                  <tr key={lang} className={`border-b border-[var(--color-border)] ${isOverall ? "border-t-2 border-t-[var(--color-border-light)] font-medium" : ""}`}>
                    <td className={`py-2 pr-4 capitalize sticky left-0 bg-[var(--color-card)] ${isOverall ? "text-[var(--color-foreground)]" : "text-[var(--color-foreground)]"}`}>
                      {isOverall ? "Overall" : lang}
                    </td>
                    {values.map((v, i) => (
                      <td key={i} className="py-2 px-2 text-right tabular-nums">
                        <span className={v === minVal && v > 0 ? "text-[var(--color-success)] font-medium" : "text-[var(--color-foreground)]"}>
                          {v.toFixed(2)}%
                        </span>
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p className="text-[9px] text-[var(--color-muted)] mt-3">
            * = our model &middot; green = best in row &middot; all scores computed with identical v1 normalization pipeline
          </p>
        </div>
      )}

      {/* Numeric Impact Table */}
      {active.length > 0 && (
        <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-lg p-5 overflow-x-auto">
          <h3 className="text-xs font-medium text-[var(--color-foreground)] tracking-wide mb-1">
            Numeric Samples Impact
          </h3>
          <p className="text-[10px] text-[var(--color-muted)] mb-4">
            WER recalculated after excluding all samples containing digits or number words in either reference or hypothesis
          </p>
          <table className="w-full text-[11px]">
            <thead>
              <tr className="border-b border-[var(--color-border)]">
                <th className="text-left font-normal text-[var(--color-muted)] pb-2 pr-4">Model</th>
                <th className="text-right font-normal text-[var(--color-muted)] pb-2 px-2">Dropped</th>
                <th className="text-right font-normal text-[var(--color-muted)] pb-2 px-2">WER Norm</th>
                <th className="text-right font-normal text-[var(--color-muted)] pb-2 px-2">WER (no num)</th>
                <th className="text-right font-normal text-[var(--color-muted)] pb-2 px-2">Delta</th>
                <th className="text-right font-normal text-[var(--color-muted)] pb-2 px-2">MER</th>
                <th className="text-right font-normal text-[var(--color-muted)] pb-2 px-2">MER (no num)</th>
                <th className="text-right font-normal text-[var(--color-muted)] pb-2 px-2">Delta</th>
              </tr>
            </thead>
            <tbody>
              {active
                .map((o) => {
                  const ov = compareData[o.modelId]?.[o.checkpoint]?.["__overall__"];
                  if (!ov) return null;
                  const wn = ov.wer_norm ?? 0;
                  const wnn = ov.wer_norm_nonum ?? wn;
                  const m = ov.mer ?? 0;
                  const mn = ov.mer_nonum ?? m;
                  const isOurs = (OUR_MODELS as readonly string[]).includes(o.modelId);
                  // Get dropped count from the metrics (stored in __overall__)
                  // We read it from the raw field if available
                  const dropped = Math.round(((wn - wnn) / wn) * 6000) || 0; // approximate
                  return { o, wn, wnn, m, mn, isOurs, dropped: wnn !== wn };
                })
                .filter(Boolean)
                .sort((a, b) => (a!.wnn) - (b!.wnn))
                .map((entry) => {
                  if (!entry) return null;
                  const { o, wn, wnn, m, mn, isOurs } = entry;
                  const wDelta = wn - wnn;
                  const mDelta = m - mn;
                  return (
                    <tr key={`${o.modelId}/${o.checkpoint}`} className="border-b border-[var(--color-border)] hover:bg-[var(--color-card-hover)] transition-colors">
                      <td className="py-2 pr-4 whitespace-nowrap" style={{ color: MODEL_COLORS[o.modelId] }}>
                        {o.label}{isOurs ? "*" : ""}
                      </td>
                      <td className="py-2 px-2 text-right text-[var(--color-muted)] tabular-nums">
                        {wDelta > 0.01 ? "yes" : "—"}
                      </td>
                      <td className="py-2 px-2 text-right text-[var(--color-foreground)] tabular-nums">{wn.toFixed(2)}%</td>
                      <td className="py-2 px-2 text-right text-[var(--color-foreground)] tabular-nums">{wnn.toFixed(2)}%</td>
                      <td className="py-2 px-2 text-right tabular-nums">
                        <span className={wDelta > 0.5 ? "text-[var(--color-success)]" : "text-[var(--color-muted)]"}>
                          {wDelta > 0.01 ? `-${wDelta.toFixed(2)}` : "—"}
                        </span>
                      </td>
                      <td className="py-2 px-2 text-right text-[var(--color-foreground)] tabular-nums">{m.toFixed(2)}%</td>
                      <td className="py-2 px-2 text-right text-[var(--color-foreground)] tabular-nums">{mn.toFixed(2)}%</td>
                      <td className="py-2 px-2 text-right tabular-nums">
                        <span className={mDelta > 0.5 ? "text-[var(--color-success)]" : "text-[var(--color-muted)]"}>
                          {mDelta > 0.01 ? `-${mDelta.toFixed(2)}` : "—"}
                        </span>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
